import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { chime, isChimeMuted, setChimeMuted } from '../lib/chime';
import NotificationsBell from './NotificationsBell';

/**
 * Global floating navigation dock — Back/Forward + sound toggle.
 * Shown app-wide except on the very first landing (path = "/").
 */
export default function NavDock() {
  const navigate = useNavigate();
  const location = useLocation();
  const [muted, setMuted] = React.useState(isChimeMuted());

  if (location.pathname === '/') return null;
  if (location.pathname.startsWith('/admin')) return null;
  // Hide the floating dock inside the Editor — it blocks the hierarchy panel's bottom entries.
  if (location.pathname.startsWith('/editor/')) return null;

  const toggleMute = () => {
    const v = !muted;
    setChimeMuted(v);
    setMuted(v);
    if (!v) chime('info');
  };

  return (
    <div
      className="fixed bottom-20 left-4 z-40 flex items-center gap-1 bg-[#0A0A0B]/90 backdrop-blur border border-[#1E1E24] rounded-full px-1.5 py-1 shadow-lg md:bottom-4"
      data-testid="nav-dock"
    >
      <button
        onClick={() => navigate(-1)}
        aria-label="Back"
        title="Back"
        className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5"
        data-testid="nav-back"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button
        onClick={() => navigate(1)}
        aria-label="Forward"
        title="Forward"
        className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5"
        data-testid="nav-forward"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
      <div className="w-px h-4 bg-[#1E1E24] mx-1" />
      <button
        onClick={toggleMute}
        aria-label="Toggle sound"
        title={muted ? 'Unmute chimes' : 'Mute chimes'}
        className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-[#06B6D4] hover:bg-white/5"
        data-testid="nav-sound-toggle"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
      <NotificationsBell compact direction="up" />
    </div>
  );
}
